import { formatPrice } from '@shared/utils';
import { useState } from 'react';
import { useI18n } from 'src/i18n/i18nContext';
import { giftCardAPI } from 'src/services/api';

type Props = {
  giftCardCode: string;
  setGiftCardCode: React.Dispatch<React.SetStateAction<string>>;
  appliedGiftCard: boolean;
  setAppliedGiftCard: React.Dispatch<React.SetStateAction<boolean>>;
  giftCardBalance: number | null;
  setGiftCardBalance: React.Dispatch<React.SetStateAction<number | null>>;
  onRemoveGiftcard: () => void;
  pricingBreakdown: {
    amountAfterPromo: number;
    giftCardDeductionAmount: number;
    finalPrice: number;
    remainingGiftCardBalance: number;
  };
};

const Giftcard: React.FC<Props> = (props) => {
  const {
    appliedGiftCard,
    setAppliedGiftCard,
    setGiftCardCode,
    giftCardCode,
    giftCardBalance,
    setGiftCardBalance,
    onRemoveGiftcard,
    pricingBreakdown,
  } = props;

  const [giftCardError, setGiftCardError] = useState<string | null>(null);
  const [applyingGiftCard, setApplyingGiftCard] = useState(false);

  const { t } = useI18n();
  const { amountAfterPromo, giftCardDeductionAmount, finalPrice, remainingGiftCardBalance } =
    pricingBreakdown;

  const handleApplyGiftCard = async () => {
    if (!giftCardCode.trim()) {
      setGiftCardError(t.booking.enterGiftCardCode);
      return;
    }

    setApplyingGiftCard(true);
    setGiftCardError(null);

    try {
      const response = await giftCardAPI.getBalance(giftCardCode.trim());
      setGiftCardBalance(response.data.balance);
      setAppliedGiftCard(true);
      setGiftCardError(null);
    } catch (err: any) {
      setGiftCardError(
        err.response?.status === 404 ? t.booking.invalidGiftCardCode : t.booking.giftCardError
      );
      setGiftCardBalance(null);
      setAppliedGiftCard(false);
    } finally {
      setApplyingGiftCard(false);
    }
  };

  const handleRemoveGiftCard = () => {
    onRemoveGiftcard();
    setGiftCardError(null);
  };

  return (
    <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
      <h3 className="mb-4 text-lg font-semibold">{t.booking.giftCard || 'Gift Card'}</h3>
      <div className="space-y-4">
        {!appliedGiftCard ? (
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {t.giftCard.have || 'Have a gift card?'}
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={giftCardCode}
                onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                placeholder="GIFT-XXXX-XXXX-XXXX"
                className={`flex-1 rounded-md border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 ${
                  giftCardError ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={applyingGiftCard}
              />
              <button
                onClick={handleApplyGiftCard}
                disabled={applyingGiftCard || !giftCardCode.trim()}
                className={`rounded-md px-4 py-2 font-medium ${
                  applyingGiftCard || !giftCardCode.trim()
                    ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}
              >
                {applyingGiftCard ? t.booking.applying : t.booking.apply}
              </button>
            </div>
            {giftCardError && <p className="mt-1 text-sm text-red-600">{giftCardError}</p>}
          </div>
        ) : (
          <div className="rounded-md border border-green-200 bg-green-50 p-4">
            <div className="mb-3 flex items-start justify-between">
              <div>
                <p className="font-medium text-green-800">
                  {t.giftCard.applied || 'Gift card applied'}
                </p>
                <p className="text-sm text-green-600">
                  {t.booking.balance || 'Balance'}: {formatPrice(Number(giftCardBalance || 0))}
                </p>
              </div>
              <button
                onClick={handleRemoveGiftCard}
                className="text-sm font-medium text-red-600 hover:text-red-800"
              >
                {t.booking.remove}
              </button>
            </div>
            <div className="space-y-1 text-sm text-green-800">
              <p>
                {t.checkout?.subtotal}: {formatPrice(amountAfterPromo)}
              </p>
              <p>
                {t.checkout?.giftCardDiscount}: -{formatPrice(giftCardDeductionAmount)}
              </p>
              <p className="font-semibold">
                {t.checkout?.total}: {formatPrice(finalPrice)}
              </p>
              <p>
                {t.booking.remainingBalance}: {formatPrice(remainingGiftCardBalance)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default Giftcard;
